import salesFlatRatesService from '@/services/salesFlatRates';
import paymentPlansService from '@/services/paymentPlans';
import salesService from '@/services/sales';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { formatCurrency } from '@/utils/numberFormat';
import { formatDateForApi } from '@/utils/datePresentation';
import {
  createDefaultSalesToolbarFilters,
  normalizeSalesToolbarFilters,
  matchesSalesSearch,
} from '@/composables/sales/useSalesFilters';
import {
  buildCreateSaleRequestData,
  buildUpdateSaleRequestData,
} from '@/composables/sales/useSalesCreateModal';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';
import { resolveSalesStatusCategory } from '@/composables/shared/useSalesStatus';
import { normalizePaymentPlanRow } from '@/composables/paymentPlans/usePaymentPlanNormalization';

const createFlatRatesCurrentYearFilters = () => {
  const now = new Date();
  const year = now.getFullYear();

  return {
    ...createDefaultSalesToolbarFilters(),
    date_start: formatDateForApi(new Date(year, 0, 1)),
    date_end: formatDateForApi(new Date(year, 11, 31)),
  };
};

const createInitialFlatRatesMetrics = () => ({
  active_flat_rates: 0,
  monthly_recurring_income: 0,
  pending_invoices: 0,
  pending_payments: 0,
});

const mapFlatRatesMetricsPayload = (payload = {}) => ({
  active_flat_rates: Number(payload?.active_flat_rates || 0),
  monthly_recurring_income: Number(payload?.monthly_recurring_income || 0),
  pending_invoices: Number(payload?.pending_invoices || 0),
  pending_payments: Number(payload?.pending_payments || 0),
});

const extractSnapshotMetrics = (response) => {
  const payload = extractPayload(response);
  const metrics = payload && typeof payload === 'object'
    ? payload.metrics
    : null;

  if (!metrics || typeof metrics !== 'object') {
    return createInitialFlatRatesMetrics();
  }

  return mapFlatRatesMetricsPayload(metrics);
};

export const createSalesFlatRatesPageState = (text = {}) => {
  const initialFilters = createFlatRatesCurrentYearFilters();

  return {
    headerActions: [
      {
        key: 'new-flat-rate',
        label: text?.headerActions?.newFlatRate,
        icon: 'fa-plus',
        variant: 'primary',
      },
    ],
    tableLoading: false,
    rowsAll: [],
    businessRowsAll: [],
    metrics: createInitialFlatRatesMetrics(),
    activeStatusTab: 'flat-rates',
    toolbarSearch: '',
    toolbarFilters: initialFilters,
    appliedToolbarSearch: '',
    appliedToolbarFilters: initialFilters,
    sortField: 'month',
    sortDirection: 'desc',
    businessSortField: 'sale_date',
    businessSortDirection: 'desc',
    currentPage: 1,
    pageSize: 30,
    deletingSale: null,
    deletingTarget: 'sale',
    deletingSaleSource: 'table',
    isDeleteConfirmModalOpen: false,
    showCreateFlatRateModal: false,
    saleModalMode: 'create',
    saleModalKind: 'flat-rate',
    saleModalInitialData: null,
    editingSaleId: 0,
    createFlatRateSubmitting: false,
    isStatusModalOpen: false,
    statusModalSubmitting: false,
    selectedSaleForStatus: null,
    statusOptions: [],
    isPaymentPlanModalOpen: false,
    isPaymentPlanSubmitting: false,
    editingPaymentPlan: null,
    editingPaymentSaleId: 0,
    editingPaymentSalePrice: 0,
    editingExistingPlannedTotal: 0,
  };
};

export const salesFlatRatesPageComputed = {
  isOpenStatusTab() {
    return this.activeStatusTab === 'flat-rates';
  },
  statusTabs() {
    const flatRatesCount = this.filteredOpenRows.length;
    const openCount = this.businessRowsByCategory('open').filter((row) => matchesSalesSearch(row, this.appliedToolbarSearch)).length;
    const closedCount = this.businessRowsByCategory('closed').filter((row) => matchesSalesSearch(row, this.appliedToolbarSearch)).length;
    const canceledCount = this.businessRowsByCategory('canceled').filter((row) => matchesSalesSearch(row, this.appliedToolbarSearch)).length;

    return [
      { key: 'flat-rates', label: this.text?.tabs?.flatRates, count: flatRatesCount, showCount: true },
      { key: 'open', label: this.text?.tabs?.open, count: openCount, showCount: true },
      { key: 'closed', label: this.text?.tabs?.closed, count: closedCount, showCount: true },
      { key: 'canceled', label: this.text?.tabs?.canceled, count: canceledCount, showCount: true },
    ];
  },
  filteredOpenRows() {
    return (Array.isArray(this.rowsAll) ? this.rowsAll : []).filter((row) => {
      const statusCategory = this.resolveBusinessStatusCategory(row);
      return statusCategory === 'open' && matchesSalesSearch(row, this.appliedToolbarSearch);
    });
  },
  sortedOpenRows() {
    return sortRows(this.filteredOpenRows, {
      field: this.sortField,
      direction: this.sortDirection,
    }, {
      month: (row) => this.resolveMonthSortValue(row),
      business: (row) => this.resolveBusinessSortValue(row),
      service: (row) => this.resolveServiceSortValue(row),
      subtotal: (row) => Number(row?.subtotal ?? row?.amount ?? 0),
      total: (row) => Number(row?.total ?? row?.amount ?? 0),
      invoice: (row) => this.resolveInvoiceSortValue(row),
      payment: (row) => this.resolvePaymentSortValue(row),
    });
  },
  filteredBusinessRows() {
    return this.businessRowsByCategory(this.activeStatusTab).filter((row) => {
      return matchesSalesSearch(row, this.appliedToolbarSearch);
    });
  },
  sortedBusinessRows() {
    return sortRows(this.filteredBusinessRows, {
      field: this.businessSortField,
      direction: this.businessSortDirection,
    }, {
      sale_date: (row) => new Date(row?.sale_date || row?.date || 0).getTime(),
      customer: (row) => String(row?.company_name || row?.customer_name || row?.cliente || '').trim().toLowerCase(),
      sale_name: (row) => String(row?.sale_name || row?.project || row?.name || row?.code || '').trim().toLowerCase(),
      service: (row) => String(row?.service_name || row?.service || row?.service_type || '').trim().toLowerCase(),
      subtotal: (row) => Number(row?.subtotal || 0),
      balance: (row) => Number(row?.balance ?? row?.saldo ?? 0),
    });
  },
  activeRows() {
    return this.isOpenStatusTab ? this.sortedOpenRows : this.sortedBusinessRows;
  },
  totalPages() {
    return resolveTotalPages(this.activeRows.length, this.pageSize);
  },
  paginatedRows() {
    return sliceRowsByPage(this.activeRows, this.currentPage, this.pageSize);
  },
  tablePagination() {
    return buildPaginationPayload({
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalItems: this.activeRows.length,
    });
  },
  businessTableEmptyText() {
    if (this.activeStatusTab === 'open') {
      return this.text?.table?.emptyOpen || this.text?.table?.empty;
    }

    return this.activeStatusTab === 'closed'
      ? this.text?.table?.emptyClosed
      : this.text?.table?.emptyCanceled;
  },
  kpiCards() {
    return [
      {
        title: this.text.metrics.activeFlatRates,
        value: String(Number(this.metrics?.active_flat_rates || 0)),
        icon: 'fa-check-circle',
        iconBg: 'rgba(34, 197, 94, 0.12)',
        iconColor: '#16a34a',
      },
      {
        title: this.text.metrics.recurringIncome,
        value: formatCurrency(this.metrics?.monthly_recurring_income || 0),
        icon: 'fa-refresh',
        iconBg: 'rgba(0, 112, 205, 0.12)',
        iconColor: '#0070cd',
      },
      {
        title: this.text.metrics.pendingInvoices,
        value: String(Number(this.metrics?.pending_invoices || 0)),
        icon: 'fa-file-text-o',
        iconBg: 'rgba(245, 158, 11, 0.12)',
        iconColor: '#d97706',
      },
      {
        title: this.text.metrics.pendingPayments,
        value: String(Number(this.metrics?.pending_payments || 0)),
        icon: 'fa-credit-card',
        iconBg: 'rgba(239, 68, 68, 0.12)',
        iconColor: '#dc2626',
      },
    ];
  },
  deleteConfirmMessage() {
    const saleName = String(this.deletingSale?.sale_name || this.deletingSale?.name || '').trim() || this.text.confirms.delete.fallbackName;
    return this.text.confirms.delete.message(saleName);
  },
};

export const salesFlatRatesPageMethods = {
  onHeaderAction(action) {
    if (String(action?.key || '') === 'new-flat-rate') {
      this.saleModalMode = 'create';
      this.saleModalKind = 'flat-rate';
      this.saleModalInitialData = null;
      this.editingSaleId = 0;
      this.showCreateFlatRateModal = true;
    }
  },
  closeCreateFlatRateModal(force = false) {
    if (!force && this.createFlatRateSubmitting) {
      return;
    }

    this.showCreateFlatRateModal = false;
    this.saleModalMode = 'create';
    this.saleModalKind = 'flat-rate';
    this.saleModalInitialData = null;
    this.editingSaleId = 0;
  },
  async handleCreateFlatRate(payload) {
    this.createFlatRateSubmitting = true;

    try {
      const isEditMode = this.saleModalMode === 'edit' && Number(this.editingSaleId || 0) > 0;
      const requestData = isEditMode
        ? buildUpdateSaleRequestData(payload)
        : buildCreateSaleRequestData(payload);

      const response = isEditMode
        ? await salesFlatRatesService.update(Number(this.editingSaleId), requestData)
        : await salesService.flatRates(requestData);

      const apiErrorMessage = extractApiErrorMessage(
        response,
        isEditMode
          ? (this.text?.toasts?.updateError || 'No fue posible actualizar el negocio.')
          : this.text.toasts.createError,
      );

      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      const createdSale = extractPayload(response);
      const createdSaleId = Number(createdSale?.id || createdSale?.sale_id || 0);

      this.$toast?.success(
        extractApiSuccessMessage(
          response,
          isEditMode
            ? (this.text?.toasts?.updateSuccess || 'El negocio ha sido actualizado correctamente.')
            : this.text.toasts.createSuccess,
        ),
      );

      this.closeCreateFlatRateModal(true);

      if (!isEditMode && createdSaleId > 0) {
        this.$router.push({ name: 'saleDetail', params: { saleId: createdSaleId } }).catch(() => {});
        return;
      }

      if (!isEditMode) {
        this.activeStatusTab = 'flat-rates';
      }

      this.currentPage = 1;
      await this.refreshRowsByFilters();
    } catch (error) {
      const fallbackError = this.saleModalMode === 'edit'
        ? (this.text?.toasts?.updateError || 'No fue posible actualizar el negocio.')
        : this.text.toasts.createError;
      this.$toast?.error(extractApiErrorMessage(error, fallbackError));
    } finally {
      this.createFlatRateSubmitting = false;
    }
  },
  resolveMonthSortValue(row) {
    const periodRaw = String(row?.period || '').trim();
    const periodMatch = periodRaw.match(/^(\d{4})-(\d{2})$/);
    if (periodMatch) {
      const year = Number(periodMatch[1]);
      const month = Number(periodMatch[2]);
      if (month >= 1 && month <= 12) {
        return new Date(year, month - 1, 1).getTime();
      }
    }

    const dateRaw = String(row?.payment_date || '').trim();
    const date = new Date(dateRaw);
    if (!Number.isNaN(date.getTime())) {
      return date.getTime();
    }

    return 0;
  },
  resolveBusinessSortValue(row) {
    return [
      row?.customer_name,
      row?.sale_code,
      row?.sale_name,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .join(' ');
  },
  resolveServiceSortValue(row) {
    return [row?.service_type, row?.product_name]
      .map((value) => String(value || '').trim().toLowerCase())
      .join(' ');
  },
  resolveInvoiceSortValue(row) {
    return [
      row?.invoice_date,
      row?.invoice_reference,
      row?.simple_note_reference,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .join(' ');
  },
  resolvePaymentSortValue(row) {
    return [row?.payment_date, row?.payment_reference]
      .map((value) => String(value || '').trim().toLowerCase())
      .join(' ');
  },
  buildRemoteListParams() {
    const normalized = normalizeSalesToolbarFilters(this.appliedToolbarFilters);

    return {
      date_start: String(normalized.date_start || '').trim(),
      date_end: String(normalized.date_end || '').trim(),
      service: normalized.service,
      status: normalized.status,
      customer: normalized.customer,
      seller: normalized.seller,
      owner: normalized.owner,
      area: normalized.area,
    };
  },
  resolveBusinessStatusCategory(row) {
    return resolveSalesStatusCategory(row);
  },
  businessRowsByCategory(statusCategory) {
    const category = String(statusCategory || '').trim().toLowerCase();
    const rows = Array.isArray(this.businessRowsAll) ? this.businessRowsAll : [];

    if (category === 'open' || category === 'closed' || category === 'canceled') {
      return rows.filter((row) => this.resolveBusinessStatusCategory(row) === category);
    }

    return rows;
  },
  ensureValidPage() {
    this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
  },
  async fetchOpenRows({ silent = false } = {}) {
    if (!silent) {
      this.tableLoading = true;
    }

    try {
      const response = await salesFlatRatesService.index(this.buildRemoteListParams());
      const rows = extractCollection(response);
      this.rowsAll = Array.isArray(rows) ? rows : [];
      this.metrics = extractSnapshotMetrics(response);
    } catch (error) {
      this.rowsAll = [];
      this.metrics = createInitialFlatRatesMetrics();
      if (!silent) {
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadRowsError));
      }
    } finally {
      if (!silent) {
        this.tableLoading = false;
      }
    }

    this.ensureValidPage();
  },
  async fetchBusinessRows({ silent = false } = {}) {
    if (!silent) {
      this.tableLoading = true;
    }

    try {
      const response = await salesFlatRatesService.businesses(this.buildRemoteListParams());
      const rows = extractCollection(response);
      this.businessRowsAll = Array.isArray(rows) ? rows : [];
    } catch (error) {
      this.businessRowsAll = [];
      if (!silent) {
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadRowsError));
      }
    } finally {
      if (!silent) {
        this.tableLoading = false;
      }
    }

    this.ensureValidPage();
  },
  async fetchRows() {
    if (this.isOpenStatusTab) {
      await this.fetchOpenRows();
      return;
    }

    await this.fetchBusinessRows();
  },
  async refreshRowsByFilters() {
    await Promise.all([
      this.fetchOpenRows({ silent: !this.isOpenStatusTab }),
      this.fetchBusinessRows({ silent: this.isOpenStatusTab }),
    ]);
  },
  async handleToolbarFilters(filters, meta = {}) {
    const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
    const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
    const normalized = normalizeSalesToolbarFilters(filters);
    this.toolbarFilters = normalized;

    if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
      this.toolbarSearch = String(normalized.search || '');
    }

    const nextDateStart = String(normalized.date_start || '').trim();
    const nextDateEnd = String(normalized.date_end || '').trim();
    const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
    const trigger = String(meta?.trigger || '').trim();

    if (dateChanged && trigger === 'date') {
      this.appliedToolbarFilters = normalized;
      this.appliedToolbarSearch = String(this.toolbarSearch || '');
      this.currentPage = 1;
      await this.refreshRowsByFilters();
    }
  },
  async applyToolbarFilters(payload = {}) {
    const search = Object.prototype.hasOwnProperty.call(payload, 'search')
      ? String(payload.search || '')
      : String(this.toolbarSearch || '');

    const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
      ? normalizeSalesToolbarFilters(payload.filters)
      : normalizeSalesToolbarFilters(this.toolbarFilters);

    this.toolbarSearch = search;
    this.toolbarFilters = filters;
    this.appliedToolbarSearch = search;
    this.appliedToolbarFilters = filters;
    this.currentPage = 1;
    await this.refreshRowsByFilters();
  },
  handleToolbarSearch(value) {
    const nextSearch = String(value || '');
    this.toolbarSearch = nextSearch;
    this.appliedToolbarSearch = nextSearch;
    this.currentPage = 1;
    this.ensureValidPage();
  },
  async selectStatusTab(nextTab) {
    const next = String(nextTab || '').trim().toLowerCase();
    if (!['flat-rates', 'open', 'closed', 'canceled'].includes(next)) {
      return;
    }

    if (next === this.activeStatusTab) {
      return;
    }

    this.activeStatusTab = next;
    this.currentPage = 1;

    if (next === 'flat-rates') {
      if (this.rowsAll.length === 0) {
        await this.fetchOpenRows();
      } else {
        this.ensureValidPage();
      }
      return;
    }

    if (this.businessRowsAll.length === 0) {
      await this.fetchBusinessRows();
    } else {
      this.ensureValidPage();
    }
  },
  handlePageChange(page) {
    this.currentPage = Number(page || 1);
    this.ensureValidPage();
  },
  handleSortChange(payload = {}) {
    const nextSort = resolveSortChange(
      {
        field: this.sortField,
        direction: this.sortDirection,
      },
      payload,
      ['month', 'business', 'service', 'subtotal', 'total', 'invoice', 'payment'],
    );

    if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
      return;
    }

    this.sortField = nextSort.field;
    this.sortDirection = nextSort.direction;
    this.currentPage = 1;
    this.ensureValidPage();
  },
  handleBusinessSortChange(payload = {}) {
    const nextSort = resolveSortChange(
      {
        field: this.businessSortField,
        direction: this.businessSortDirection,
      },
      payload,
      ['sale_date', 'customer', 'sale_name', 'service', 'subtotal', 'balance'],
    );

    if (nextSort.field === this.businessSortField && nextSort.direction === this.businessSortDirection) {
      return;
    }

    this.businessSortField = nextSort.field;
    this.businessSortDirection = nextSort.direction;
    this.currentPage = 1;
    this.ensureValidPage();
  },
  resolveStatusSaleId(row) {
    return Number(row?.sale_id || row?.id || 0);
  },
  async handleChangeSaleStatus(row) {
    const saleId = this.resolveStatusSaleId(row);
    if (saleId <= 0) {
      return;
    }

    this.selectedSaleForStatus = {
      ...row,
      id: saleId,
    };
    this.isStatusModalOpen = true;
    await this.loadStatusOptions();
  },
  handleBusinessChangeSaleStatus(row) {
    this.handleChangeSaleStatus(row);
  },
  closeStatusModal(force = false) {
    if (!force && this.statusModalSubmitting) {
      return;
    }

    this.isStatusModalOpen = false;
    this.selectedSaleForStatus = null;
  },
  resolveSelectedSaleName() {
    return String(this.selectedSaleForStatus?.sale_name || this.selectedSaleForStatus?.name || '').trim();
  },
  resolveSelectedSaleStatusLabel() {
    return String(this.selectedSaleForStatus?.status_name || this.selectedSaleForStatus?.status || '').trim();
  },
  resolveSelectedSaleStatusId() {
    return Number(this.selectedSaleForStatus?.status_sale_id || 0);
  },
  async loadStatusOptions() {
    if (this.statusOptions.length > 0) {
      return;
    }

    try {
      const response = await salesService.statuses({}, { skipLoading: true });
      const collection = extractCollection(response);
      this.statusOptions = normalizeCatalogOptions(collection, { useCodeInLabel: false });
    } catch (error) {
      this.statusOptions = [];
      this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.statusOptionsLoadError));
    }
  },
  async confirmUpdateSaleStatus(statusSaleId) {
    const saleId = this.resolveStatusSaleId(this.selectedSaleForStatus);
    const nextStatusId = Number(statusSaleId || 0);
    if (saleId <= 0 || nextStatusId <= 0) {
      return;
    }

    this.statusModalSubmitting = true;

    try {
      const response = await salesFlatRatesService.updateStatus(saleId, {
        status_sale_id: nextStatusId,
      });

      const apiError = extractApiErrorMessage(response, this.text.toasts.statusUpdateError);
      if (apiError) {
        this.$toast?.error(apiError);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, this.text.toasts.statusUpdateSuccess));
      this.closeStatusModal(true);
      await this.refreshRowsByFilters();
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.statusUpdateError));
    } finally {
      this.statusModalSubmitting = false;
    }
  },
  async handleBusinessEditSale(row) {
    const saleId = this.resolveStatusSaleId(row);
    if (saleId <= 0) {
      return;
    }

    try {
      const response = await salesService.show(saleId, {}, { skipLoading: true });
      const detailPayload = extractPayload(response);
      const detail = detailPayload && typeof detailPayload?.data === 'object'
        ? detailPayload.data
        : detailPayload;

      if (!detail || typeof detail !== 'object') {
        this.$toast?.error(this.text?.toasts?.businessDetailLoadError || 'No fue posible cargar el negocio para edición.');
        return;
      }

      this.saleModalMode = 'edit';
      this.saleModalKind = 'business';
      this.editingSaleId = saleId;
      this.saleModalInitialData = detail;
      this.showCreateFlatRateModal = true;
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, this.text?.toasts?.businessDetailLoadError || 'No fue posible cargar el negocio para edición.'));
    }
  },
  handleBusinessDeleteSale(row) {
    const saleId = this.resolveStatusSaleId(row);
    if (saleId <= 0) {
      return;
    }

    this.deletingSale = {
      ...(row && typeof row === 'object' ? row : {}),
      id: saleId,
      sale_id: saleId,
    };
    this.deletingTarget = 'sale';
    this.deletingSaleSource = 'table';
    this.isDeleteConfirmModalOpen = true;
  },
  handleInvoiceSale(row) {
    const paymentId = Number(row?.payment_id || row?.id || 0);
    if (paymentId <= 0) {
      return;
    }

    this.$router.push({
      name: 'invoiceCreate',
      query: {
        payment_id: String(paymentId),
      },
    }).catch(() => {});
  },
  handleSimpleNoteSale() {
    this.$toast?.info(this.text.toasts.invoiceFlowPending);
  },
  handlePaySale() {
    this.$toast?.info(this.text.toasts.payFlowPending);
  },
  closePaymentPlanModal(force = false) {
    if (!force && this.isPaymentPlanSubmitting) {
      return;
    }

    this.isPaymentPlanModalOpen = false;
    this.editingPaymentPlan = null;
    this.editingPaymentSaleId = 0;
    this.editingPaymentSalePrice = 0;
    this.editingExistingPlannedTotal = 0;
  },
  async handleEditSale(row) {
    const paymentId = Number(row?.payment_id || row?.id || 0);
    const saleId = Number(row?.sale_id || 0);
    if (paymentId <= 0 || saleId <= 0) {
      return;
    }

    try {
      const [detailResult, saleResult, plansResult] = await Promise.allSettled([
        paymentPlansService.show(paymentId),
        salesService.show(saleId, {}, { skipLoading: true }),
        paymentPlansService.index({ sale_id: saleId }, { skipLoading: true }),
      ]);

      if (detailResult.status !== 'fulfilled') {
        throw detailResult.reason || new Error('PAYMENT_PLAN_DETAIL_ERROR');
      }

      const detailResponse = detailResult.value;
      const saleResponse = saleResult.status === 'fulfilled' ? saleResult.value : null;
      const plansResponse = plansResult.status === 'fulfilled' ? plansResult.value : null;

      const detailPayload = extractPayload(detailResponse);
      const detail = detailPayload && typeof detailPayload?.data === 'object'
        ? detailPayload.data
        : detailPayload;

      const salePayload = extractPayload(saleResponse);
      const sale = salePayload && typeof salePayload?.data === 'object'
        ? salePayload.data
        : salePayload;

      const plansRows = extractCollection(plansResponse)
        .map((planRow, index) => normalizePaymentPlanRow(planRow, index));

      this.editingPaymentPlan = normalizePaymentPlanRow(detail && typeof detail === 'object' ? detail : row);
      this.editingPaymentSaleId = saleId;
      this.editingPaymentSalePrice = Number(sale?.sale_price || sale?.subtotal || row?.amount || 0) || 0;
      this.editingExistingPlannedTotal = plansRows
        .reduce((acc, planRow) => acc + (Number(planRow?.subtotal || 0) || 0), 0);

      if (this.editingExistingPlannedTotal <= 0) {
        this.editingExistingPlannedTotal = Number(this.editingPaymentPlan?.subtotal || row?.amount || 0) || 0;
      }

      this.isPaymentPlanModalOpen = true;
    } catch (error) {
      this.$toast?.error(this.text.toasts.paymentPlanDetailLoadError);
    }
  },
  async submitPaymentPlanEdit(payload) {
    const planId = Number(this.editingPaymentPlan?.id || 0);
    if (planId <= 0) {
      return;
    }

    this.isPaymentPlanSubmitting = true;

    try {
      const requestPayload = {
        ...payload,
        sale_id: Number(this.editingPaymentSaleId || payload?.sale_id || 0),
      };

      const response = await paymentPlansService.update(planId, requestPayload);
      const apiErrorMessage = extractApiErrorMessage(response, this.text.toasts.paymentPlanUpdateError);
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(this.text.toasts.paymentPlanUpdateSuccess);
      this.closePaymentPlanModal(true);
      await this.refreshRowsByFilters();
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.paymentPlanUpdateError));
    } finally {
      this.isPaymentPlanSubmitting = false;
    }
  },
  handleViewSale(row) {
    const saleId = Number(row?.sale_id || row?.id || 0);
    if (saleId <= 0) {
      return;
    }

    this.$router.push({ name: 'saleDetail', params: { saleId } }).catch(() => {});
  },
  handleDeleteSale(row) {
    this.deletingSale = row;
    this.deletingTarget = 'payment-plan';
    this.deletingSaleSource = 'table';
    this.isDeleteConfirmModalOpen = true;
  },
  handleDeleteSaleFromModal(sale) {
    const saleId = Number(sale?.id || sale?.sale_id || this.editingSaleId || 0);
    if (saleId <= 0) {
      return;
    }

    this.deletingSale = {
      ...(sale && typeof sale === 'object' ? sale : {}),
      id: saleId,
      sale_id: saleId,
      sale_name: String(
        sale?.sale_name
        || sale?.name
        || this.saleModalInitialData?.sale_name
        || this.saleModalInitialData?.name
        || ''
      ).trim(),
    };
    this.deletingTarget = 'sale';
    this.deletingSaleSource = 'modal';
    this.isDeleteConfirmModalOpen = true;
  },
  closeDeleteConfirmModal() {
    this.deletingSale = null;
    this.deletingTarget = 'sale';
    this.deletingSaleSource = 'table';
    this.isDeleteConfirmModalOpen = false;
  },
  async confirmDeleteSale() {
    const isPaymentPlanDelete = String(this.deletingTarget || '').trim().toLowerCase() === 'payment-plan';

    if (isPaymentPlanDelete) {
      const paymentId = Number(this.deletingSale?.payment_id || this.deletingSale?.id || 0);
      if (paymentId <= 0) {
        this.closeDeleteConfirmModal();
        return;
      }

      const planDeleteError = this.text?.toasts?.paymentPlanDeleteError || 'No fue posible eliminar el plan de pagos.';
      const planDeleteSuccess = this.text?.toasts?.paymentPlanDeleteSuccess || 'El plan de pagos ha sido eliminado correctamente.';

      try {
        const response = await paymentPlansService.delete(paymentId);
        const apiError = extractApiErrorMessage(response, planDeleteError);
        if (apiError) {
          this.$toast?.error(apiError);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, planDeleteSuccess));
        this.closeDeleteConfirmModal();
        await this.refreshRowsByFilters();
      } catch (error) {
        this.$toast?.error(extractApiErrorMessage(error, planDeleteError));
      }

      return;
    }

    const saleId = this.resolveStatusSaleId(this.deletingSale);
    if (saleId <= 0) {
      this.closeDeleteConfirmModal();
      return;
    }

    try {
      const response = await salesService.delete(saleId);
      const apiError = extractApiErrorMessage(response, this.text.toasts.deleteError);
      if (apiError) {
        this.$toast?.error(apiError);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, this.text.toasts.deleteSuccess));

      if (this.deletingSaleSource === 'modal') {
        this.closeCreateFlatRateModal(true);
      }

      this.closeDeleteConfirmModal();
      await this.refreshRowsByFilters();
    } catch (error) {
      this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.deleteError));
    }
  },
};
